'use client';

import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { Headphones } from 'lucide-react';
import { staticUrl } from '@/shared/static';
import { VideoFrame } from './VideoFrame';

/**
 * "How it works" as THREE steps, not seven.
 *
 * Every shot is the actual wizard captured IN THE VISITOR'S LANGUAGE
 * (`public/landing/<locale>/…`, produced by scripts/capture-landing-shots.mjs),
 * so a German visitor never sees a Russian UI. Re-run that script after a
 * wizard redesign — a stale screenshot here is a promise the product no longer
 * keeps.
 */

/**
 * Five steps and two "choices" underneath them described the old wizard, and a
 * tester read the whole block as a list of chores. A first-time parent now meets
 * ONE screen, so the landing shows what that screen actually asks: who it is for,
 * what it is about, and what comes back. Three cards, two of them real screens of
 * the first-story form, the third the finished story itself.
 */
const STEPS = [
  { key: 'who', shot: '10-first' },
  { key: 'what', shot: '11-idea' },
  { key: 'get', shot: null },
] as const;

/** Locales we actually captured screens for; anything else falls back to English. */
const SHOT_LOCALES = new Set(['ru', 'en', 'uk', 'cs', 'de', 'fr']);
/** Locales whose demo story has a cover our pipeline drew. */
const DEMO_COVERS = new Set(['ru', 'en', 'uk', 'cs', 'de', 'fr']);

/**
 * The screenshots are the product's own 390px-wide screens, captured at 2×. They
 * are shown small here ON PURPOSE: this block says "three steps", and the reading
 * of the screens happens further down, where the same story is told with the real
 * output (the showcase). At full phone size these three frames alone were a fifth
 * of the page on a phone, and the visitor met the same explanation three times
 * before reaching any reason to act.
 */
function Phone({ src, alt, size = 260 }: { src: string; alt: string; size?: number }) {
  return (
    <div className="mx-auto w-full" style={{ maxWidth: size }}>
      <div className="relative aspect-[780/1328] overflow-hidden rounded-[30px] border-[6px] border-foreground/85 bg-foreground/85 shadow-xl">
        <Image
          src={src}
          alt={alt}
          fill
          sizes={`(max-width: 640px) 88vw, ${size}px`}
          quality={90}
          className="rounded-[24px] object-cover object-top"
        />
      </div>
    </div>
  );
}

export default function HowItWorksSection({
  video,
}: {
  /** A screen recording of the real flow — shown INSTEAD of the three stills.
   *  A film of somebody doing it answers "how does it work" in one press; three
   *  frozen screens ask the visitor to imagine the movement between them. */
  video?: { id: string; poster: string } | null;
} = {}) {
  const t = useTranslations('howItWorks');
  const locale = useLocale();
  const shotLocale = SHOT_LOCALES.has(locale) ? locale : 'en';
  const shotUrl = (name: string) => staticUrl(`/landing/${shotLocale}/${name}.png`);
  // The finished story shown in step 3 — the same cover the hero plays.
  const cover = DEMO_COVERS.has(locale) ? staticUrl(`/landing/demo/cover-${locale}.webp`) : staticUrl('/landing/hero-cover.webp');

  return (
    <section className="pb-14 lg:pb-20 bg-muted/30" data-testid="landing-how-it-works">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* With a recording of its own this is a section, not a footnote: it
            gets the question as a heading and the promise under it. */}
        <h2 className="mb-2 text-center text-3xl font-bold sm:text-4xl">{t('title')}</h2>
        <p className="mb-8 text-center text-lg text-muted-foreground">{t('description')}</p>

        {video ? (
          /* The recording carries the screens; the steps stand beside it as the
             words, so neither repeats the other three times over. */
          <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,20rem)_1fr] lg:gap-14">
            <div className="mx-auto w-full max-w-[20rem]">
              <VideoFrame
                videoId={video.id}
                poster={video.poster}
                soonLabel={t('watch')}
                playLabel={t('watch')}
                vertical
              />
            </div>
            <ol className="space-y-6 lg:pt-6">
              {STEPS.map((step, i) => (
                <li key={step.key} className="flex gap-3" data-testid={`landing-step-${step.key}`}>
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <span>
                    <span className="block text-lg font-bold leading-tight">{t(`steps.${step.key}.title`)}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">
                      {t(`steps.${step.key}.description`)}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        ) : (
        <ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((step, i) => (
            /* h-full + mt-auto on the picture: the titles run one or two lines,
               and without this the frames sat at different heights and the row
               looked like it was bouncing. */
            <li key={step.key} className="flex h-full flex-col" data-testid={`landing-step-${step.key}`}>
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {i + 1}
                </span>
                <h3 className="text-lg font-bold leading-tight">{t(`steps.${step.key}.title`)}</h3>
              </div>
              <p className="mb-5 text-sm text-muted-foreground">{t(`steps.${step.key}.description`)}</p>
              {step.shot ? (
                <div className="mt-auto">
                  <Phone src={shotUrl(step.shot)} alt={t(`steps.${step.key}.title`)} />
                </div>
              ) : (
                /* The last step is the RESULT, so it shows the story rather than a
                   screen of the app: the cover our own pipeline drew for this
                   language, with what comes with it stated on top of it. */
                <div className="mx-auto mt-auto w-full max-w-[340px]">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border shadow-xl">
                    <Image
                      src={cover}
                      alt={t('steps.get.title')}
                      fill
                      sizes="(max-width: 640px) 88vw, 340px"
                      className="object-cover"
                    />
                    <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur">
                      <Headphones className="size-3.5" aria-hidden />
                      {t('steps.get.badge')}
                    </span>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ol>
        )}
      </div>
    </section>
  );
}
